import type { ReactNode } from 'react';
import { niceMax, ticks } from './scale';
import './charts.css';

export interface TrendPoint {
  /** A short label for the x axis: "Mon", "12 Sep", "W38". Only every nth is drawn. */
  label: string;
  value: number;
  /** The same point one period earlier, drawn as a dashed ghost when given. */
  previous?: number;
}

export interface TrendProps {
  points: readonly TrendPoint[];
  /** Spoken summary of the whole picture. */
  label: string;
  /** Formats an axis value: 1200 → "1.2k". */
  format?: (n: number) => string;
  width?: number;
  height?: number;
}

const PAD = { top: 8, right: 8, bottom: 22, left: 36 };

function polyline(
  values: readonly (number | undefined)[],
  top: number,
  w: number,
  h: number,
): string {
  const inner = { w: w - PAD.left - PAD.right, h: h - PAD.top - PAD.bottom };
  const stepX = values.length > 1 ? inner.w / (values.length - 1) : 0;
  return values
    .map((v, i) => {
      if (v === undefined) return null;
      const x = PAD.left + i * stepX;
      const y = PAD.top + inner.h * (1 - v / top);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .filter((p): p is string => p !== null)
    .join(' ');
}

/**
 * A line over time, this period solid and the previous one dashed, with a light grid. Nothing for
 * no points: the screen says why in words.
 */
export function Trend({
  points,
  label,
  format = String,
  width = 480,
  height = 160,
}: TrendProps): ReactNode {
  if (points.length === 0) return null;
  const values = points.map((p) => p.value);
  const ghosts = points.map((p) => p.previous);
  const top = niceMax([...values, ...ghosts.filter((g): g is number => g !== undefined)]);
  const inner = { w: width - PAD.left - PAD.right, h: height - PAD.top - PAD.bottom };
  const line = polyline(values, top, width, height);
  const ghost = ghosts.some((g) => g !== undefined) ? polyline(ghosts, top, width, height) : '';
  return (
    <svg className="pui-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <Grid top={top} width={width} innerH={inner.h} format={format} />
      {line ? (
        <polygon
          className="pui-chart__area"
          points={`${PAD.left},${PAD.top + inner.h} ${line} ${width - PAD.right},${PAD.top + inner.h}`}
        />
      ) : null}
      {ghost ? (
        <polyline className="pui-chart__line pui-chart__line--ghost" points={ghost} />
      ) : null}
      {line ? <polyline className="pui-chart__line" points={line} /> : null}
      <AxisLabels points={points} innerW={inner.w} height={height} />
    </svg>
  );
}

/** The tick values whose formatted text is new; an all-zero series otherwise reads "0 0 1 1". */
function distinctTicks(top: number, format: (n: number) => string): number[] {
  const seen = new Set<string>();
  return ticks(top, 4).filter((t) => {
    const text = format(t);
    if (seen.has(text)) return false;
    seen.add(text);
    return true;
  });
}

/** Three light lines with their values, so the eye has a scale without a frame. */
function Grid({
  top,
  width,
  innerH,
  format,
}: {
  top: number;
  width: number;
  innerH: number;
  format: (n: number) => string;
}): ReactNode {
  return (
    <>
      {distinctTicks(top, format).map((t) => {
        const y = PAD.top + innerH * (1 - t / top);
        return (
          <g key={t}>
            <line className="pui-chart__grid" x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} />
            <text x={PAD.left - 6} y={y + 4} textAnchor="end">
              {format(t)}
            </text>
          </g>
        );
      })}
    </>
  );
}

/**
 * Which points get a label along the bottom: about six, evenly spaced, always the last, and never
 * one so close to the last that the two would overlap (14 points: 0, 3, 6, 9 and 13, not 12 too).
 */
export function labelledPoints(count: number): number[] {
  if (count === 0) return [];
  const every = Math.max(1, Math.ceil(count / 6));
  const last = count - 1;
  const out: number[] = [];
  for (let i = 0; i < last; i += every) if (last - i >= every / 2) out.push(i);
  out.push(last);
  return out;
}

/** About six labels along the bottom, always including the last point. */
function AxisLabels({
  points,
  innerW,
  height,
}: {
  points: readonly TrendPoint[];
  innerW: number;
  height: number;
}): ReactNode {
  const shown = new Set(labelledPoints(points.length));
  const xOf = (i: number) =>
    PAD.left + (points.length > 1 ? (i * innerW) / (points.length - 1) : 0);
  return (
    <>
      {points.map((p, i) =>
        shown.has(i) ? (
          <text key={p.label + i} x={xOf(i)} y={height - 6} textAnchor="middle">
            {p.label}
          </text>
        ) : null,
      )}
    </>
  );
}
